/**
 * Hook: useSyncQueue
 * Exposes the current state of the offline sync queue.
 * Polls IndexedDB every 2 seconds for an up-to-date count and item list.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  getAll,
  getPendingCount,
  remove,
  processQueue,
  type SyncQueueItem,
} from '../services/syncQueue';

interface UseSyncQueueResult {
  /** Total number of items with status 'pending' */
  pendingCount: number;
  /** All items in the queue (pending + failed) */
  items: SyncQueueItem[];
  /** Manually trigger a queue replay */
  processQueue: () => Promise<void>;
  /** Remove a specific item by id */
  removeItem: (id: number) => Promise<void>;
  /** Refresh the queue state immediately */
  refresh: () => Promise<void>;
}

export function useSyncQueue(): UseSyncQueueResult {
  const [pendingCount, setPendingCount] = useState(0);
  const [items, setItems] = useState<SyncQueueItem[]>([]);

  const refresh = useCallback(async () => {
    const [count, all] = await Promise.all([getPendingCount(), getAll()]);
    setPendingCount(count);
    setItems(all);
  }, []);

  // Poll for changes every 2 seconds
  useEffect(() => {
    let cancelled = false;

    void refresh();

    const id = setInterval(() => {
      if (!cancelled) void refresh();
    }, 2_000);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [refresh]);

  const handleProcessQueue = useCallback(async () => {
    await processQueue();
    await refresh();
  }, [refresh]);

  const removeItem = useCallback(
    async (id: number) => {
      await remove(id);
      await refresh();
    },
    [refresh]
  );

  return {
    pendingCount,
    items,
    processQueue: handleProcessQueue,
    removeItem,
    refresh,
  };
}
